import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { modalidadesService } from '../services/modalidades.service'
import type { ModalidadeFilters } from '../types'
import { cacheModalidades, getCachedModalidades } from '../../../lib/offlineDb'

export const MODALIDADES_KEY = 'modalidades'
export const REGRAS_DESEMPATE_KEY = 'regras-desempate'

export function useModalidades(filters: ModalidadeFilters = {}) {
  return useQuery({
    queryKey: [MODALIDADES_KEY, filters],
    queryFn: async () => {
      const data = await modalidadesService.list(filters)
      await cacheModalidades(data.data as unknown[])
      return data
    },
  })
}

export function useCachedModalidades() {
  return useQuery({
    queryKey: [MODALIDADES_KEY, 'cached'],
    queryFn: async () => {
      const cached = await getCachedModalidades()
      return cached as unknown[]
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export function useCreateModalidade() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: modalidadesService.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: [MODALIDADES_KEY] }),
  })
}

export function useUpdateModalidade() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => modalidadesService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [MODALIDADES_KEY] }),
  })
}

export function useDeleteModalidade() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => modalidadesService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [MODALIDADES_KEY] }),
  })
}

export function useRegrasDesempate(idmodalidade: number | null) {
  return useQuery({
    queryKey: [REGRAS_DESEMPATE_KEY, idmodalidade],
    queryFn: () => modalidadesService.listRegras(idmodalidade!),
    enabled: idmodalidade !== null,
  })
}

export function useAplicarPadrao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (idmodalidade: number) => modalidadesService.aplicarPadrao(idmodalidade),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: [REGRAS_DESEMPATE_KEY, id] }),
  })
}

export function useCreateRegraDesempate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ idmodalidade, data }: { idmodalidade: number; data: any }) =>
      modalidadesService.createRegra(idmodalidade, data),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: [REGRAS_DESEMPATE_KEY, vars.idmodalidade] }),
  })
}

export function useDeleteRegraDesempate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, idmodalidade }: { id: number; idmodalidade: number }) =>
      modalidadesService.deleteRegra(id),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: [REGRAS_DESEMPATE_KEY, vars.idmodalidade] }),
  })
}
